import { BROKER_SYNC_ISSUE_KIND_LABELS } from "@signals/shared";
import type { BrokerSyncIssuesResponse, BrokerSyncAccountStatus } from "./broker-sync-panel.types";
import { t, localeKey, formatDateTime } from "./broker-sync-panel-format.util";
import { redactKnownSecrets, sanitizePayload } from "./broker-sync-panel-error.util";

export function BrokerSyncIssues({
  issues,
  accounts,
  errorText,
  isLoading,
  managementCode,
  secretValues,
}: {
  issues: BrokerSyncIssuesResponse | undefined;
  accounts: readonly BrokerSyncAccountStatus[];
  errorText: string | null;
  isLoading: boolean;
  managementCode: string;
  secretValues: readonly string[];
}) {
  if (isLoading) return <p className="text-sm text-muted-foreground">{t("portfolio.brokerSync.issuesLoading")}</p>;
  if (errorText) {
    return <p role="alert" className="text-sm text-destructive">{t("portfolio.brokerSync.issuesError")} {errorText}</p>;
  }
  const items = issues?.items ?? [];
  const accountNames = new Map(accounts.map((account) => [account.accountId, account.accountName]));
  return (
    <div className="space-y-3">
      {issues?.truncated ? (
        <p className="text-sm text-amber-700">
          {t("portfolio.brokerSync.issuesTruncated", { shown: items.length, total: issues.total })}
        </p>
      ) : null}
      {items.length === 0 ? <p className="text-sm text-muted-foreground">{t("portfolio.brokerSync.noIssues")}</p> : null}
      {items.map((issue, index) => {
        const issueType = BROKER_SYNC_ISSUE_KIND_LABELS[issue.kind]?.[localeKey()] ?? issue.kind;
        const safeText = redactKnownSecrets(issue.text, [managementCode, ...secretValues]);
        const payload = sanitizePayload(issue.payload);
        return (
          <article key={issue.id ?? `${issue.accountId}:${issue.operationKey}:${index}`} className="space-y-2 rounded-md border p-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h4 className="font-medium">{issueType}</h4>
              <time className="text-xs text-muted-foreground" dateTime={issue.lastDetectedAt}>
                {t("portfolio.brokerSync.issueDetected")}: {formatDateTime(issue.lastDetectedAt)}
              </time>
            </div>
            <p className="text-xs text-muted-foreground">{accountNames.get(issue.accountId) ?? issue.accountId}</p>
            <p className="text-sm">{safeText}</p>
            <details>
              <summary className="cursor-pointer text-sm text-muted-foreground">{t("portfolio.brokerSync.issueDetails")}</summary>
              <p className="mt-2 break-all text-xs text-muted-foreground">
                {t("portfolio.brokerSync.operationKey")}: <code>{issue.operationKey}</code>
              </p>
              <pre className="mt-2 max-h-64 overflow-auto rounded-md bg-muted p-3 text-xs">{JSON.stringify(payload, null, 2)}</pre>
            </details>
          </article>
        );
      })}
    </div>
  );
}
